import { MacroGraphMessageType, macroGraphControlSchema, macroGraphServiceControlSchema,
  macroGraphResponseSchema, macroGraphServiceResponseSchema, macroGraphEventSchema, macroGraphTestEventSchema,
  type ConnectedHost, type PlaycastMessage, type PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type { IMessageRouter } from '../../services/MessageRouter';
import { NodeServiceGraphChannel, type INodeGraphConnection } from '../../services/macro-graph/NodeServiceGraphChannel';
import { InteractiveSequenceRelay } from './InteractiveSequenceRelay';
import { MacroGraphRelayCoordinator } from './MacroGraphRelayCoordinator';

export interface MacroGraphRelayComposition {
  ipc: IIpcService; realtime: IRealtimeService; router: IMessageRouter;
  receive(listener: (message: PlaycastMessage<PlaycastMessageTarget>) => void): void;
  owns(host: ConnectedHost): boolean; host(targetSessionId: string): ConnectedHost | null;
  requestTimeoutMs: number;
  connection: INodeGraphConnection;
}
/** Uses existing NodeService credentials/socket and the selected interactive native IPC connection. */
export function initializeMacroGraphs(dependencies: MacroGraphRelayComposition): MacroGraphRelayCoordinator {
  const targets = { owns: (target: string) => { const host = dependencies.host(target); return !!host && dependencies.owns(host); } };
  const channel = new NodeServiceGraphChannel(dependencies.connection, dependencies.requestTimeoutMs);
  const native = new InteractiveSequenceRelay(channel, dependencies.ipc, targets);
  const relay = new MacroGraphRelayCoordinator(channel, native, targets); relay.initialize();
  dependencies.receive(message => {
    if (native.receiveNative(message)) return;
    if (message.header.target === 'host' && String(message.header.action) === 'notifyPlayjectorDisconnect') native.nativeDisconnected();
  });
  const envelope = () => ({ timestamp: Date.now(), id: crypto.randomUUID() });
  dependencies.router.register(MacroGraphMessageType.CONTROL, raw => {
    const parsed = macroGraphControlSchema.safeParse(raw); if (!parsed.success || !dependencies.owns(parsed.data.selectedHost)) return;
    const request = parsed.data;
    void relay.run(request.selectedHost.sessionId, request.command).then(reply => {
      dependencies.realtime.trySendMessage(macroGraphResponseSchema.parse({ ...envelope(), type: MacroGraphMessageType.RESPONSE,
        selectedHost: request.selectedHost, ...reply }));
    }, error => {
      const code = error instanceof Error && /^[a-z0-9_]{1,128}$/.test(error.message) ? error.message : 'graph_relay_failed';
      dependencies.realtime.trySendMessage(macroGraphResponseSchema.parse({ ...envelope(), type: MacroGraphMessageType.RESPONSE,
        selectedHost: request.selectedHost, requestId: request.command.requestId, snapshot: null, errorCode: code }));
    });
  });
  dependencies.router.register(MacroGraphMessageType.SERVICE_CONTROL, raw => {
    const parsed = macroGraphServiceControlSchema.safeParse(raw); if (!parsed.success || !dependencies.owns(parsed.data.selectedHost)) return;
    const request = parsed.data;
    void relay.trusted(request.operation).then(result => dependencies.realtime.trySendMessage(macroGraphServiceResponseSchema.parse({
      ...envelope(), type: MacroGraphMessageType.SERVICE_RESPONSE, selectedHost: request.selectedHost, result }))).catch(() => undefined);
  });
  relay.subscribe(event => {
    const selectedHost = dependencies.host(event.targetSessionId);
    if (selectedHost?.kind === 'remote' && dependencies.owns(selectedHost)) dependencies.realtime.trySendMessage(macroGraphEventSchema.parse({
      ...envelope(), type: MacroGraphMessageType.EVENT, selectedHost, event }));
  });
  relay.subscribeTesting(event => {
    const selectedHost = event.identity.targetSessionId ? dependencies.host(event.identity.targetSessionId) : null;
    if (selectedHost?.kind === 'remote' && dependencies.owns(selectedHost)) dependencies.realtime.trySendMessage(macroGraphTestEventSchema.parse({
      ...envelope(), type: 'MACROGRAPH_TEST_EVENT', selectedHost, event }));
  });
  return relay;
}
